import React, { useMemo, useCallback, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useHistory } from 'react-router';
import { useLazyQuery, useApolloClient } from '@apollo/client';
import { useRootStore } from 'store';
import { Card } from 'react-bootstrap';
import { CellFloatCustom, CellLeft, FlexRow, TableInMem, exportXlsModal } from 'motion-components';
import LoadingOverlay from '@achmadk/react-loading-overlay';
import { useTranslation } from 'react-i18next';
import gql from 'graphql-tag';
import { formatFloat } from 'shared-pages/utils/helpers';
import { Filters } from './filters';

export const gqlGetContributionItemsDetails = gql`
  query getPeriodSumOfContributionsItem(
    $itemId: Int
    $fromPeriodId: Int
    $fromYear: Int
    $tillPeriodId: Int
    $tillYear: Int
  ) {
    getContributionItemsDetails(
      itemId: $itemId
      fromPeriodId: $fromPeriodId
      fromYear: $fromYear
      tillPeriodId: $tillPeriodId
      tillYear: $tillYear
    ) {
      data {
        contributorId
        contributorName
        vatNumber
        qty1
        units1
        qty2
        units2
        price
      }
      recordCount
    }
  }
`;

/* ----------- Page ------------------ */
export const ContributionItemDetailsPage = () => {
  const { appStore } = useRootStore();
  const client = useApolloClient();
  const { t } = useTranslation();

  /* ----------------- Route State -------------------------- */
  const location = useLocation<any>();
  const history = useHistory();
  // Στο state του routing αποθηκεύω:
  //    1. την τρέχουσα σελίδα του table pagination (pageIndex)
  //  & 2. τα values των φίλτρων (filterValues)

  /// Αρχικές Τιμές Στα Φίλτρα
  useEffect(() => {
    const values = location?.state?.filterValues ?? {
      itemId: 1,
      fromPeriodId: 1 /*1oΤριμηνο*/,
      fromYear: new Date().getFullYear(),
      tillPeriodId: 4 /*4oΤριμηνο*/,
      tillYear: new Date().getFullYear(),
    };
    if (!values.year) {
      values.year = new Date().getFullYear();
      // Ενημέρωσε το State του Route
      history.replace(location.pathname, {
        ...location.state,
        filterValues: values,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [queryParams, setQueryParams] = useState({
    itemId: parseInt(location?.state?.filterValues.itemId) ?? 1,
    fromYear: parseInt(location?.state?.filterValues.fromYear) ?? 2030,
    tillYear: parseInt(location?.state?.filterValues.tillYear) ?? 2030,
    fromPeriodId: parseInt(location?.state?.filterValues.fromPeriodId) ?? 1,
    tillPeriodId: parseInt(location?.state?.filterValues.tillPeriodId) ?? 4,
  });

  const [footerValues, setFooterValues] = useState({
    qty1: 0,
    qty2: 0,
    price: 0,
  });

  const [getData, { loading, data: dataRaw }] = useLazyQuery(gqlGetContributionItemsDetails, {
    fetchPolicy: 'no-cache',
    onCompleted: (data) => {
      // Υπολόγισε αθροίσματα
      if (data[Object.keys(data)[0]]?.data) {
        let qty1 = 0;
        let qty2 = 0;
        let price = 0;
        for (let index = 0; index < data[Object.keys(data)[0]]?.data.length; index++) {
          const values = data[Object.keys(data)[0]]?.data[index];
          qty1 += values.qty1;
          qty2 += values.qty2;
          price += values.price;
        }
        setFooterValues({ qty1, qty2, price });
      }
    },
  });
  const data = dataRaw ? dataRaw[Object.keys(dataRaw)[0]]?.data : undefined;
  const recordCount = dataRaw ? dataRaw[Object.keys(dataRaw)[0]]?.recordCount : undefined;

  const filtersChanged = useCallback(
    (values) => {
      // Σύνθεσε τα vars του graphql
      const newQueryParams = {
        ...queryParams,
        itemId: parseInt(values.itemId),
        fromYear: parseInt(values.fromYear),
        tillYear: parseInt(values.tillYear),
        fromPeriodId: parseInt(values.fromPeriodId),
        tillPeriodId: parseInt(values.tillPeriodId),
      };

      setQueryParams(newQueryParams); // αποθήκευσε τα vars
      // φερε τα δεδομένα απο τον σερβερ
      getData({ variables: { ...newQueryParams } });
    },
    [getData, queryParams],
  );

  function useExcelGetQuery() {
    return useLazyQuery(gqlGetContributionItemsDetails);
    //return [null, { loading: null, data: null }];
  }
  const handleExport = useCallback(async () => {
    const filename = `Εισφορές ανα Κατηγορία ${queryParams.fromYear} -  ${queryParams.tillYear}`;
    await exportXlsModal({
      apolloClient: client,
      tableName: undefined,
      getLazyQuery: useExcelGetQuery,
      filterVariables: queryParams,
      exportFileName: filename,
      columnsDef: [
        { fieldName: 'contributorName', fieldTitle: 'Υπόχρεος', fieldType: 'TEXT' },
        { fieldName: 'vatNumber', fieldTitle: 'Α.Φ.Μ.', fieldType: 'TEXT' },
        { fieldName: 'qty1', fieldTitle: 'Ποσότητα 1', fieldType: 'FLOAT' },
        { fieldName: 'units1', fieldTitle: 'Μονάδες 1', fieldType: 'TEXT' },
        { fieldName: 'qty2', fieldTitle: 'Ποσότητα 2', fieldType: 'FLOAT' },
        { fieldName: 'units2', fieldTitle: 'Μονάδες 2', fieldType: 'TEXT' },
        { fieldName: 'price', fieldTitle: 'Εισφορά σε ευρώ', fieldType: 'FLOAT' },
      ],
    });
  }, [queryParams]);

  const columns = useMemo(
    () => [
      {
        accessor: 'contributorName',
        Header: 'Υπόχρεος',
        Cell: CellLeft,
      },
      {
        accessor: 'vatNumber',
        Header: 'Α.Φ.Μ.',
        Cell: CellLeft,
      },
      {
        accessor: 'qty1',
        Header: 'Ποσότητα (ΤΜΧ)',
        Cell: ({ row }) => (
          <div className="text-center">
            {formatFloat(row.original.qty1, 2)} {row.original.units1}
          </div>
        ),
        Footer: () => {
          return <div className="text-center">{formatFloat(footerValues.qty1, 2)}</div>;
        },
      },
      {
        accessor: 'qty2',
        Header: 'Βάρος (kg)',
        Cell: ({ row }) => (
          <div className="text-center">
            {formatFloat(row.original.qty2, 2)} {row.original.units2}
          </div>
        ),
        Footer: () => {
          return <div className="text-center">{formatFloat(footerValues.qty2, 2)}</div>;
        },
      },
      {
        accessor: 'price',
        Header: 'Εισφορά (€)',
        Cell: CellFloatCustom(5, 2),
        Footer: () => {
          return CellFloatCustom(5, 2)({ cell: { value: footerValues.price } });
        },
      },
    ],
    [footerValues.price, footerValues.qty1, footerValues.qty2],
  );
  return (
    <Card className="shadow">
      <Card.Header>
        <FlexRow>
          <Card.Title className="m-0">
            {t('Εισφορές ανά Κατηγορία και Υπόχρεο Αναλυτικά')}
          </Card.Title>
        </FlexRow>
      </Card.Header>
      <Card.Body className="nopadding">
        {/* Φίλτρα */}
        <Filters onChange={filtersChanged} initialValues={location?.state?.filterValues ?? {}} />
        {/* TableGrid */}
        <LoadingOverlay active={loading} spinner text={t('Παρακαλώ περιμένετε...')}>
          {data ? (
            <TableInMem
              //onRefresh={() => refetch()}
              onExport={handleExport}
              columns={columns}
              data={data}
              hasSearch={false}
              hasFooter={true}
              propPageSize={50}
            />
          ) : (
            <div className="empty-table-container"></div>
          )}
        </LoadingOverlay>
      </Card.Body>
    </Card>
  );
};
